import { normalizeQcm } from '../normalizers/normalizeQcm';
import { estTableau } from './reponseLibre';

/**
 * Colonnage des sous-questions d'un bloc à figure partagée ('figure-group') :
 * mêmes principes que la grille de réponses (.ep-grille-reponses) — le JS ne
 * fournit que la largeur NÉCESSAIRE d'une sous-question légère, le CSS décide
 * du nombre de colonnes (2 maxi) selon la place réelle, zoom compris. Une
 * sous-question « lourde » (support large : tableau, classement, relier,
 * figures) occupe toute la rangée (grid-column 1 / -1).
 *
 * Les besoins sont des ESTIMATIONS : surestimé → 1 colonne (moins compact),
 * sous-estimé → le contenu wrappe dans sa colonne. Jamais de débordement :
 * la formule CSS plafonne à min(besoin, 100%).
 */

/** Plafond de colonnes de sous-questions (feuille A4 : au-delà, illisible). */
export const CAP_COLONNES_FIGGROUP = 2;

const stripHtml = (s) => String(s || '').replace(/<[^>]*>/g, ' ');

/** Longueur (caractères) de la plus longue ligne d'un HTML simple. */
const maxLigne = (html) => stripHtml(html)
    .split(/\n/)
    .reduce((m, l) => Math.max(m, l.trim().length), 0);

/** Une formule `$…$` englobant un trou ne se replie JAMAIS : dans une
 *  demi-rangée elle déborderait en silence (aucun scan ne surveille
 *  l'intérieur d'un bloc pleine largeur, la racine clippe). */
const KATEX_ENGLOBE_TROU_RE = /\$[^$]*\{\{[^}]+\}\}[^$]*\$/;

/** Sous-question qui exige toute la largeur de sa rangée. */
export const itemLourdFigure = (payload) => {
    if (estTableau(payload)) return true;
    if (payload?.type === 'association') {
        // Seul le texte à trous se plie bien en demi-rangée ; classement,
        // relier, ordonner (chips en ligne), figures : pleine rangée.
        if (payload?.contenu?.subType !== 'texte_trou') return true;
        return KATEX_ENGLOBE_TROU_RE.test(String(payload?.contenu?.phrase || ''));
    }
    return false;
};

/**
 * Largeur nécessaire (px) d'une sous-question LÉGÈRE. L'énoncé peut se
 * REPLIER sur plusieurs lignes dans sa colonne (contrairement aux rangées de
 * la grille de réponses) : sa contribution est plafonnée à ~36 caractères.
 * Seule la largeur incompressible du CORPS (une option de QCM, la phrase à
 * trous) peut encore exiger la rangée entière. Clampée : plancher lisible,
 * plafond au-delà duquel une rangée complète s'impose de toute façon.
 */
const TEXTE_CAP_CHARS = 36;

export const besoinItemFigure = (payload) => {
    const texteMax = Math.min(maxLigne(payload?.texte), TEXTE_CAP_CHARS);
    let corps = 0;
    if (payload?.type === 'qcm') {
        // Une option par rangée interne au minimum (lettre + texte ≈ 34 px
        // de chrome) — la grille interne des options replie le reste.
        const { options } = normalizeQcm(payload);
        const optMax = options.reduce(
            (m, o) => Math.max(m, o.kind === 'text' ? (o.content?.length || 0) : 20),
            0
        );
        corps = optMax * 7 + 34;
    } else if (payload?.type === 'association' && payload?.contenu?.subType === 'texte_trou') {
        // La phrase à trous ne se replie pas au milieu d'un trou : sa plus
        // longue ligne (trous ≈ 60 px pièce) est incompressible.
        const phrase = String(payload?.contenu?.phrase || '');
        const nTrous = (phrase.match(/\{\{[^}]+\}\}/g) || []).length;
        corps = maxLigne(phrase.replace(/\{\{[^}]+\}\}/g, '')) * 7 + nTrous * 60;
    }
    const besoin = Math.max(texteMax * 7 + 24, corps);
    return Math.max(200, Math.min(620, besoin));
};

/** Besoin du CONTENEUR : max des besoins des sous-questions légères
 *  (0 si aucune — la grille restera mono-colonne via le fallback 100%). */
export const besoinGrilleFigure = (items = []) => items.reduce(
    (m, { payload }) => (itemLourdFigure(payload) ? m : Math.max(m, besoinItemFigure(payload))),
    0
);
